import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import beautify from 'js-beautify';
import { Button, Tooltip } from 'antd';
import { CopyOutlined, CaretDownFilled, CaretUpFilled } from '@ant-design/icons';
import CopyToClipboard from 'react-copy-to-clipboard';
import CodeMirror from '@uiw/react-codemirror';
import { materialInit } from '@uiw/codemirror-theme-material';
import { xml } from '@codemirror/lang-xml';
import { json } from '@codemirror/lang-json';

const useStyles = createUseStyles({
  editorWrapper: {
    marginRight: '10px',
    minHeight: '500px',
    width: 'calc(100vw - 520px)',
    maxWidth: 'calc(100vw - 520px)',
  },
  editorWrapperExpanded: {
    marginRight: '10px',
    maxHeight: '100% !important',
    height: '100% !important',
    width: 'calc(100vw - 520px)',
    maxWidth: 'calc(100vw - 520px)',
    '& .CodeMirror': {
      height: '100% !important',
    },
  },
  editorWrapperHideShow: {
    marginRight: '10px',
    width: '100% !important',
    maxWidth: 'calc(100vw)',
  },
  editorWrapperFull: {
    marginRight: '10px',
    maxHeight: '100% !important',
    height: '100% !important',
    width: '100% !important',
    maxWidth: 'calc(100vw)',
    '& .CodeMirror': {
      height: '95% !important',
    },
  },
});

export interface StyledCodeMirrorProps {
  title: string | React.ReactNode;
  initialValue: string;
  mode: 'javascript' | 'xml' | 'text';
  readOnly: boolean;
  full?: boolean;
  hide?: boolean;
  onEditorChange?: (val: string) => void;
  validate?: boolean;
  extra?: React.ReactNode;
  contained?: boolean;
  children?: React.ReactNode;
}

const formatCodeForMode = (mode: string, data: string) => {
  switch (mode) {
    case 'javascript':
      return beautify(data, { indent_size: 2 });
    case 'xml': {
      return beautify.html_beautify(data, { indent_size: 2, wrap_line_length: 200 });
    }
    default:
      return data;
  }
};

export const StyledCodeMirror = ({
  title,
  initialValue,
  mode,
  full,
  hide,
  readOnly,
  validate,
  extra,
  onEditorChange,
  contained,
  children,
}: StyledCodeMirrorProps) => {
  const classes = useStyles();
  const [expanded, toggleExpand] = useState(false);
  const [show, toggleShow] = useState(!hide);
  const [contentValidationMessage, setContentValidationMessage] = useState<string | null>();
  const [debounceTimeout, setdebounceTimeout] = useState<NodeJS.Timeout | null>();

  const formatted = formatCodeForMode(mode, initialValue);
  const [content, setContent] = useState(formatted);

  useEffect(() => {
    //update on initial value change
    setContent(formatCodeForMode(mode, initialValue));
  }, [initialValue, mode]);

  let wrapper = classes.editorWrapper;
  if (hide) {
    wrapper = classes.editorWrapperHideShow;
  } else if (full) {
    wrapper = classes.editorWrapperFull;
  } else if (expanded) {
    wrapper = classes.editorWrapperExpanded;
  }

  const containerWidth = contained ? '100%' : 'calc(100vw - 520px)';
  const containerStyle: React.CSSProperties = {
    width: containerWidth,
    maxWidth: containerWidth,
  };
  const codeMirrorMaxWidth = containerWidth;

  const validateContent = (editorString: string) => {
    if (!validate) {
      return;
    }
    // TODO I highly doubt we will want to validate anything
    // besides json but I am writing this function like we might
    try {
      if (mode === 'javascript') {
        JSON.parse(editorString);
      }
      setContentValidationMessage(null);
    } catch (err) {
      setContentValidationMessage(err.message);
    }
  };

  return (
    <>
      <div>
        <h4 style={{ display: 'inline-block', marginBottom: 0 }}>{title}</h4>
        <CopyToClipboard text={content}>
          <Tooltip title={'Copy'} trigger={'hover'}>
            <Tooltip title={'Copied'} trigger={'click'}>
              <Button size={'small'} type={'link'} icon={<CopyOutlined />} />
            </Tooltip>
          </Tooltip>
        </CopyToClipboard>
        {!full && !hide && (
          <Tooltip title={expanded ? 'Use Fixed Height' : 'Expand'} trigger={'hover'}>
            <Button
              type={'link'}
              icon={expanded ? <CaretUpFilled /> : <CaretDownFilled />}
              onClick={() => toggleExpand(!expanded)}
            />
          </Tooltip>
        )}
        {hide && (
          <Tooltip title={show ? 'Hide' : 'Show'} trigger={'hover'}>
            <Button
              type={'link'}
              icon={show ? <CaretUpFilled /> : <CaretDownFilled />}
              onClick={() => toggleShow(!show)}
            />
          </Tooltip>
        )}
        {extra && <span style={{ float: 'right' }}>{extra}</span>}
      </div>
      {show && (
        <>
          {contentValidationMessage && <p style={{ color: 'red' }}>{contentValidationMessage}</p>}
          <div
            className={wrapper}
            // Override default styles for containment of json within code mirror container in shipment detail
            style={containerStyle}
          >
            <CodeMirror
              theme={materialInit({
                settings: {
                  foreground: 'white',
                },
              })}
              extensions={mode === 'javascript' ? [json()] : [xml()]}
              value={readOnly ? formatted : content}
              readOnly={readOnly}
              maxHeight={!expanded ? '85vh' : '100%'}
              maxWidth={codeMirrorMaxWidth}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLine: false,
                highlightActiveLineGutter: false,
              }}
              onChange={(value) => {
                setContent(value);
                if (debounceTimeout) {
                  clearTimeout(debounceTimeout);
                  setdebounceTimeout(null);
                }
                setdebounceTimeout(
                  setTimeout(() => {
                    validateContent(value);
                  }, 1000),
                );
                if (onEditorChange) {
                  onEditorChange(value);
                }
              }}
            />
          </div>
          {children}
        </>
      )}
    </>
  );
};

export default StyledCodeMirror;
